import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { Search, UserPlus, ArrowLeft, CheckCircle2, AlertTriangle, Phone, Sparkles, Loader2 } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import LanguageToggle from "../components/LanguageToggle";
import PhoneNumpad from "../components/PhoneNumpad";
import { api } from "../lib/nailApi";

const PIN_STORAGE = "nailEvent.checkinPin";
const STAFF_STORAGE = "nailEvent.staffName";
const BG_IMG = "https://images.pexels.com/photos/16935917/pexels-photo-16935917.jpeg";

export default function NailEventCheckIn() {
  const { slug } = useParams();
  const { lang, t } = useI18n();

  const [pin, setPin] = useState(() => sessionStorage.getItem(PIN_STORAGE) || "");
  const [pinDraft, setPinDraft] = useState("");
  const [pinError, setPinError] = useState("");
  const [staff, setStaff] = useState(() => localStorage.getItem(STAFF_STORAGE) || "");

  const [event, setEvent] = useState(null);
  const [query, setQuery] = useState("");
  const [numpadDigits, setNumpadDigits] = useState("");
  const [numpadFullscreen, setNumpadFullscreen] = useState(false);
  // Phase 8.14 hotfix — the numpad is the "active" input source only when a
  // numpad tap actually wrote to numpadDigits. If the user starts typing on
  // the keyboard, we flip this off. This lets us sync both non-empty AND
  // empty transitions of numpadDigits back into query (Clear key, auto-clear
  // after check-in) without wiping a keyboard-typed query.
  const numpadDrivenRef = useRef(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [walkinOpen, setWalkinOpen] = useState(false);
  const [walkin, setWalkin] = useState({ business_name: "", phone: "", owner_name: "", guest_count: "" });
  const [walkinSaving, setWalkinSaving] = useState(false);
  const [actionMsg, setActionMsg] = useState(null);
  const searchRef = useRef(null);

  useEffect(() => {
    api.get(`/nail-events/${slug}/public`).then((r) => setEvent(r.data)).catch(() => {});
  }, [slug]);

  // Phase 8.14 — sync numpad digits into the search query. Only fires when the
  // last update to numpadDigits came from a numpad tap (not from us zeroing it
  // out because the user started typing). Syncs both non-empty AND empty
  // transitions so Clear key + post-check-in auto-clear reach the input.
  useEffect(() => {
    if (numpadDrivenRef.current) setQuery(numpadDigits);
  }, [numpadDigits]);

  const handleNumpadChange = (d) => {
    numpadDrivenRef.current = true;
    setNumpadDigits(d);
  };

  const authHeaders = useMemo(() => ({ "X-Checkin-Pin": pin, "X-Staff-Name": staff }), [pin, staff]);

  const handlePinSubmit = async (e) => {
    e.preventDefault();
    setPinError("");
    try {
      // Validate by trying a benign search.
      await api.get(`/nail-events/${slug}/checkin/search`, {
        params: { q: "test" },
        headers: { "X-Checkin-Pin": pinDraft },
      });
      setPin(pinDraft);
      sessionStorage.setItem(PIN_STORAGE, pinDraft);
    } catch (err) {
      if (err?.response?.status === 401) setPinError(t("checkin.pin_error"));
      else setPinError(err?.message || "Error");
    }
  };

  const runSearch = useCallback(async (q) => {
    if (!q.trim()) return;
    setLoading(true);
    setSearched(true);
    try {
      const r = await api.get(`/nail-events/${slug}/checkin/search`, {
        params: { q: q.trim() },
        headers: authHeaders,
      });
      setResults(r.data.results || []);
    } catch (err) {
      setActionMsg({ type: "error", text: err?.response?.data?.detail || "Search failed" });
    } finally {
      setLoading(false);
    }
  }, [slug, authHeaders]);

  // Debounced live search
  useEffect(() => {
    if (!pin) return;
    if (!query.trim()) { setResults([]); setSearched(false); return; }
    const id = setTimeout(() => runSearch(query), 280);
    return () => clearTimeout(id);
  }, [query, pin, runSearch]);

  const handleCheckIn = async (reg) => {
    try {
      const r = await api.post(
        `/nail-events/${slug}/checkin/${reg.id}`,
        {},
        { headers: authHeaders }
      );
      const status = r.data.status;
      setActionMsg({
        type: status === "already_checked_in" ? "warn" : "success",
        text: status === "already_checked_in" ? t("checkin.already_checked_in") : `${r.data.registration.business_name} ✓`,
      });
      // Replace row in results
      setResults((rows) => rows.map((x) => x.id === reg.id ? r.data.registration : x));
      // Phase 8.14 — auto-clear the numpad after a successful check-in so the
      // next attendee starts on a blank keypad.
      if (status !== "already_checked_in") setNumpadDigits("");
    } catch (err) {
      setActionMsg({ type: "error", text: err?.response?.data?.detail || "Error" });
    }
  };

  const handleWalkinSubmit = async (e) => {
    e.preventDefault();
    if (!walkin.business_name.trim() || !walkin.phone.trim()) return;
    setWalkinSaving(true);
    try {
      const payload = {
        business_name: walkin.business_name.trim(),
        phone: walkin.phone.trim(),
        owner_name: walkin.owner_name.trim() || null,
        guest_count: walkin.guest_count ? parseInt(walkin.guest_count, 10) : null,
        preferred_language: "vi",
        checked_in_by: staff || "staff",
      };
      const r = await api.post(`/nail-events/${slug}/checkin/walkin`, payload, { headers: authHeaders });
      setActionMsg({ type: "success", text: `${r.data.registration.business_name} ✓` });
      setWalkinOpen(false);
      setWalkin({ business_name: "", phone: "", owner_name: "", guest_count: "" });
      setQuery("");
      setResults([]);
      setSearched(false);
    } catch (err) {
      setActionMsg({ type: "error", text: err?.response?.data?.detail || "Error" });
    } finally {
      setWalkinSaving(false);
    }
  };

  // PIN gate
  if (!pin) {
    return (
      <PinGate
        bgImg={BG_IMG}
        onSubmit={handlePinSubmit}
        pinDraft={pinDraft}
        setPinDraft={setPinDraft}
        pinError={pinError}
      />
    );
  }

  return (
    <div className="min-h-screen relative bg-linen text-espresso" data-testid="checkin-page">
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{ backgroundImage: `url(${BG_IMG})` }} />
        <div className="absolute inset-0 bg-linen/85 backdrop-blur-3xl" />
      </div>

      <header className="px-6 md:px-10 pt-6 md:pt-8 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Sparkles className="w-5 h-5 text-rose-accent" />
          <div>
            <p className="text-[11px] uppercase tracking-[0.28em] text-espresso-muted font-semibold">{t("checkin.title")}</p>
            <h1 className="font-serif text-2xl md:text-3xl text-espresso leading-none mt-1">
              {event?.name || "—"}
            </h1>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <input
            data-testid="staff-name-input"
            type="text"
            value={staff}
            onChange={(e) => { setStaff(e.target.value); localStorage.setItem(STAFF_STORAGE, e.target.value); }}
            placeholder={t("checkin.staff_label")}
            className="hidden md:block bg-white/80 backdrop-blur-md border border-espresso/10 rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-espresso"
          />
          <LanguageToggle />
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 md:px-10 pt-10 pb-24">
        <div className="bg-white/90 backdrop-blur-xl border border-white/70 rounded-3xl p-6 md:p-8 shadow-[0_24px_60px_-30px_rgba(44,36,27,0.35)]">
          <label className="block">
            <span className="text-[11px] uppercase tracking-[0.28em] text-espresso-soft font-semibold">
              {t("checkin.search_label")}
            </span>
            <div className="mt-3 relative">
              <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-espresso-muted" />
              <input
                ref={searchRef}
                data-testid="checkin-search-input"
                value={query}
                onChange={(e) => {
                  // Keyboard typing takes ownership away from the numpad so a
                  // downstream numpad-clear can't erase what the user typed.
                  numpadDrivenRef.current = false;
                  setQuery(e.target.value);
                  setNumpadDigits("");
                }}
                placeholder={t("checkin.search_placeholder")}
                className="w-full bg-white border border-espresso/10 rounded-2xl pl-14 pr-5 py-5 text-xl min-h-[64px] placeholder:text-espresso-muted/70 focus:outline-none focus:ring-2 focus:ring-espresso focus:border-espresso transition-all"
                autoComplete="off"
              />
              {loading && (
                <Loader2 className="absolute right-5 top-1/2 -translate-y-1/2 w-5 h-5 text-espresso-muted animate-spin" />
              )}
            </div>
          </label>

          {/* Phase 8.14 — Numpad Door Mode (inline + fullscreen toggle) */}
          <PhoneNumpad
            digits={numpadDigits}
            onDigitsChange={handleNumpadChange}
            fullscreen={numpadFullscreen}
            onFullscreenChange={setNumpadFullscreen}
            lang={lang}
          />

          {actionMsg && (
            <div
              data-testid="action-toast"
              className={`mt-4 rounded-2xl px-5 py-3 text-sm font-medium ${
                actionMsg.type === "success"
                  ? "bg-[#E1EDE6] text-[#2D4A3E] border border-[#2D4A3E]/15"
                  : actionMsg.type === "warn"
                    ? "bg-[#FCEBD4] text-[#8A5A19] border border-[#8A5A19]/15"
                    : "bg-[#F5DADA] text-[#7A2E2E] border border-[#7A2E2E]/15"
              }`}
            >
              {actionMsg.text}
            </div>
          )}
        </div>

        {/* Results */}
        <div className="mt-8 space-y-3" data-testid="checkin-results">
          {results.map((reg) => (
            <ResultCard key={reg.id} reg={reg} onCheckIn={() => handleCheckIn(reg)} />
          ))}

          {searched && !loading && results.length === 0 && (
            <NoResults onAddWalkin={() => { setWalkinOpen(true); setWalkin((w) => ({ ...w, business_name: /[a-zA-Z]/.test(query) ? query : "", phone: /\d/.test(query) ? query : "" })); }} />
          )}
        </div>

        {walkinOpen && (
          <WalkinDialog
            walkin={walkin}
            setWalkin={setWalkin}
            saving={walkinSaving}
            onSubmit={handleWalkinSubmit}
            onClose={() => setWalkinOpen(false)}
          />
        )}
      </main>
    </div>
  );
}

function PinGate({ bgImg, onSubmit, pinDraft, setPinDraft, pinError }) {
  const { t } = useI18n();
  return (
    <div className="min-h-screen relative flex items-center justify-center px-6 bg-linen">
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{ backgroundImage: `url(${bgImg})` }} />
        <div className="absolute inset-0 bg-linen/85 backdrop-blur-3xl" />
      </div>
      <div className="absolute top-6 right-6"><LanguageToggle /></div>
      <form
        onSubmit={onSubmit}
        className="w-full max-w-md bg-white/90 backdrop-blur-xl border border-white/70 rounded-3xl p-8 md:p-10 shadow-[0_24px_60px_-30px_rgba(44,36,27,0.4)]"
      >
        <div className="flex items-center gap-2 mb-6">
          <Sparkles className="w-4 h-4 text-rose-accent" />
          <span className="text-[11px] uppercase tracking-[0.28em] text-espresso-muted font-semibold">
            {t("checkin.title")}
          </span>
        </div>
        <h2 className="font-serif text-3xl md:text-4xl text-espresso">{t("checkin.pin_title")}</h2>
        <p className="text-espresso-soft mt-2">{t("checkin.pin_subtitle")}</p>
        <input
          data-testid="checkin-pin-input"
          type="password"
          autoFocus
          value={pinDraft}
          onChange={(e) => setPinDraft(e.target.value)}
          placeholder={t("checkin.pin_placeholder")}
          className="mt-6 w-full bg-white border border-espresso/10 rounded-2xl px-5 py-4 text-lg min-h-[64px] focus:outline-none focus:ring-2 focus:ring-espresso focus:border-espresso"
        />
        {pinError && (
          <p data-testid="checkin-pin-error" className="mt-3 text-sm text-[#7A2E2E]">{pinError}</p>
        )}
        <button
          type="submit"
          data-testid="checkin-pin-submit"
          className="mt-6 w-full bg-espresso text-linen rounded-full px-6 py-4 font-medium hover:bg-espresso-hover transition-all"
        >
          {t("checkin.pin_submit")}
        </button>
      </form>
    </div>
  );
}

function ResultCard({ reg, onCheckIn }) {
  const { t } = useI18n();
  const statusKey = reg.checked_in
    ? "checked_in"
    : reg.is_walkin
      ? "walkin"
      : "registered";

  const badges = {
    checked_in: { bg: "bg-[#E1EDE6]", text: "text-[#2D4A3E]", label: t("checkin.checked_in_badge"), icon: CheckCircle2 },
    walkin: { bg: "bg-[#F1E1DC]", text: "text-[#7A4D40]", label: t("checkin.walkin_badge"), icon: UserPlus },
    registered: { bg: "bg-[#EAE5D9]", text: "text-espresso", label: t("checkin.registered_badge"), icon: Phone },
  };
  const B = badges[statusKey];

  return (
    <div
      data-testid={`checkin-result-${reg.id}`}
      className="bg-white border border-espresso/10 rounded-3xl p-5 md:p-6 flex items-center justify-between gap-4 shadow-[0_8px_24px_rgba(44,36,27,0.04)]"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap mb-1.5">
          <h3 className="font-serif text-2xl md:text-3xl text-espresso truncate">{reg.business_name}</h3>
          <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${B.bg} ${B.text}`}>
            <B.icon className="w-3.5 h-3.5" /> {B.label}
          </span>
        </div>
        <div className="text-espresso-soft text-sm md:text-base flex items-center gap-x-5 gap-y-1 flex-wrap">
          {reg.owner_name && <span>{reg.owner_name}</span>}
          <span className="font-medium">{reg.phone}</span>
          {reg.guest_count != null && <span>· {reg.guest_count}</span>}
        </div>
      </div>
      <button
        type="button"
        data-testid={`checkin-button-${reg.id}`}
        disabled={reg.checked_in}
        onClick={onCheckIn}
        className={`min-h-[64px] px-7 rounded-full font-semibold transition-all duration-300 whitespace-nowrap ${
          reg.checked_in
            ? "bg-[#E1EDE6] text-[#2D4A3E] cursor-not-allowed"
            : "bg-espresso text-linen hover:bg-espresso-hover hover:-translate-y-0.5"
        }`}
      >
        {reg.checked_in ? t("checkin.checked_in_badge") : t("checkin.checkin_button")}
      </button>
    </div>
  );
}

function NoResults({ onAddWalkin }) {
  const { t } = useI18n();
  return (
    <div
      data-testid="checkin-no-results"
      className="bg-white border border-espresso/10 rounded-3xl p-8 md:p-10 text-center"
    >
      <div className="w-14 h-14 rounded-full bg-[#FCEBD4] text-[#8A5A19] mx-auto flex items-center justify-center">
        <AlertTriangle className="w-6 h-6" />
      </div>
      <h3 className="font-serif text-3xl text-espresso mt-4">{t("checkin.no_results_title")}</h3>
      <p className="text-espresso-soft mt-2">{t("checkin.no_results_subtitle")}</p>
      <button
        type="button"
        data-testid="add-walkin-button"
        onClick={onAddWalkin}
        className="mt-6 inline-flex items-center gap-2 bg-espresso text-linen rounded-full px-7 py-4 min-h-[56px] font-medium hover:bg-espresso-hover transition-all"
      >
        <UserPlus className="w-4 h-4" /> {t("checkin.add_walkin")}
      </button>
    </div>
  );
}

function WalkinDialog({ walkin, setWalkin, saving, onSubmit, onClose }) {
  const { t } = useI18n();
  const upd = (k) => (e) => setWalkin((w) => ({ ...w, [k]: e.target.value }));
  return (
    <div className="fixed inset-0 z-40 flex items-end md:items-center justify-center px-3 md:px-6 py-4 md:py-10 bg-espresso/30 backdrop-blur-sm">
      <form
        onSubmit={onSubmit}
        data-testid="walkin-form"
        className="w-full max-w-xl bg-white rounded-3xl p-6 md:p-8 shadow-[0_30px_80px_-30px_rgba(44,36,27,0.5)]"
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-serif text-3xl text-espresso">{t("checkin.walkin_title")}</h3>
          <button
            type="button"
            data-testid="walkin-close"
            onClick={onClose}
            className="text-espresso-muted hover:text-espresso text-sm inline-flex items-center gap-1"
          >
            <ArrowLeft className="w-4 h-4" /> {t("checkin.back")}
          </button>
        </div>
        <div className="space-y-4">
          <DialogField label={t("checkin.search_label")} value={walkin.business_name} onChange={upd("business_name")} placeholder="Lily Nails" testId="walkin-business" required />
          <DialogField label={t("checkin.search_placeholder")} value={walkin.phone} onChange={upd("phone")} placeholder="(555) 123-4567" testId="walkin-phone" type="tel" required />
          <div className="grid grid-cols-2 gap-3">
            <DialogField label="Owner" value={walkin.owner_name} onChange={upd("owner_name")} testId="walkin-owner" />
            <DialogField label="Guests" value={walkin.guest_count} onChange={upd("guest_count")} testId="walkin-guests" type="number" />
          </div>
        </div>
        <button
          type="submit"
          disabled={saving}
          data-testid="walkin-save"
          className="mt-6 w-full bg-espresso text-linen rounded-full px-6 py-4 min-h-[64px] font-medium hover:bg-espresso-hover disabled:opacity-60 transition-all"
        >
          {saving ? t("checkin.saving") : t("checkin.walkin_save")}
        </button>
      </form>
    </div>
  );
}

function DialogField({ label, required, testId, ...props }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-espresso-soft">
        {label} {required && <span className="text-rose-accent">*</span>}
      </span>
      <input
        data-testid={testId}
        {...props}
        className="mt-2 w-full bg-white border border-espresso/10 rounded-2xl px-4 py-3 min-h-[56px] text-base focus:outline-none focus:ring-2 focus:ring-espresso focus:border-espresso"
      />
    </label>
  );
}
